import * as React from "react";
import { NodeViewWrapper, type NodeViewProps } from "@tiptap/react";
import { cn } from "../../../lib/utils";
import { useIsEditable } from "./use-is-editable";

export const MIN_IMAGE_WIDTH = 64;
/** One press of the keyboard resize shortcut. */
export const IMAGE_RESIZE_STEP = 32;

export const IMAGE_ALIGNMENTS = ["left", "center", "right"] as const;
export type ImageAlign = (typeof IMAGE_ALIGNMENTS)[number];

/**
 * Alignment is a class, not an inline style.
 *
 * Same reasoning as `width` in reverse: resting prose is `dangerouslySetInnerHTML`
 * of the stored string, styled by `rich-text-editor.css`, so a fixed set of
 * class names is exactly what that stylesheet can carry — and `class` is
 * already on the sanitiser's allowlist while `margin` is not.
 */
export const IMAGE_ALIGN_CLASS: Record<ImageAlign, string> = {
  left: "rte-align-left",
  center: "rte-align-center",
  right: "rte-align-right",
};

/* The caption input is a form control, so it takes a utility rather than
 * inheriting the `text-align` the stylesheet gives a serialised figcaption. */
const CAPTION_ALIGN: Record<ImageAlign, string> = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
};
const CORNERS = [
  {
    key: "nw",
    position: "-left-1 -top-1",
    cursor: "cursor-nwse-resize",
    dir: -1,
  },
  {
    key: "ne",
    position: "-right-1 -top-1",
    cursor: "cursor-nesw-resize",
    dir: 1,
  },
  {
    key: "sw",
    position: "-bottom-1 -left-1",
    cursor: "cursor-nesw-resize",
    dir: -1,
  },
  {
    key: "se",
    position: "-bottom-1 -right-1",
    cursor: "cursor-nwse-resize",
    dir: 1,
  },
] as const;

/**
 * Inline image with corner resize handles and a caption.
 *
 * Read-only renders a plain figure: at rest this HTML is also injected straight
 * into the resting-prose block, so anything interactive here would be chrome a
 * reader cannot use.
 */
export function ImageNodeView({
  node,
  updateAttributes,
  editor,
  selected,
}: NodeViewProps) {
  const wrapperRef = React.useRef<HTMLElement | null>(null);
  const imgRef = React.useRef<HTMLImageElement | null>(null);
  const captionRef = React.useRef<HTMLInputElement | null>(null);
  /* Live width during a drag. Committing per pointermove would push one undo
   * step per pixel; the attribute is written exactly once, on pointerup. */
  const [dragWidth, setDragWidth] = React.useState<number | null>(null);

  const src = node.attrs.src as string;
  const alt = (node.attrs.alt as string | null) ?? "";
  /* `null` and `""` are different states: null is "no caption", `""` is an
   * empty caption the author has just opened and is about to type into. The
   * row renders on the second, never on selection — see the note above it. */
  const caption = node.attrs.caption as string | null;
  const storedWidth = node.attrs.width as string | null;
  const align = ((node.attrs.align as ImageAlign | null) ??
    "center") as ImageAlign;
  /* State, not a plain read: `InlineEdit` keeps one editor mounted and flips
   * `editable` on every click in and commit out, and nothing re-renders a node
   * view on that by itself. */
  const editable = useIsEditable(editor);

  const startResize = React.useCallback(
    (event: React.PointerEvent<HTMLSpanElement>, dir: number) => {
      const img = imgRef.current;
      if (!img) return;
      event.preventDefault();
      event.stopPropagation();
      const handle = event.currentTarget;
      handle.setPointerCapture(event.pointerId);

      const startX = event.clientX;
      const startW = img.offsetWidth;
      const max =
        wrapperRef.current?.parentElement?.clientWidth ??
        Number.MAX_SAFE_INTEGER;
      let next = startW;

      const onMove = (e: PointerEvent) => {
        const delta = (e.clientX - startX) * dir;
        next = Math.min(Math.max(startW + delta, MIN_IMAGE_WIDTH), max);
        setDragWidth(next);
      };
      const onUp = () => {
        handle.removeEventListener("pointermove", onMove);
        handle.removeEventListener("pointerup", onUp);
        handle.removeEventListener("pointercancel", onUp);
        setDragWidth(null);
        updateAttributes({ width: `${Math.round(next)}px` });
      };
      handle.addEventListener("pointermove", onMove);
      handle.addEventListener("pointerup", onUp);
      handle.addEventListener("pointercancel", onUp);
    },
    [updateAttributes],
  );

  /* An empty caption is a transient state — the row is open and waiting for
   * text. Leaving one behind would keep a blank row in the document (and in the
   * stored `<figure>`), so it collapses back to "no caption" on the way out. */
  const closeEmptyCaption = React.useCallback(() => {
    if (caption === "") updateAttributes({ caption: null });
  }, [caption, updateAttributes]);

  /* Opening a caption from the toolbar should land the caret in it; without
   * this the row appears and the author has to find it with the mouse. Runs
   * only on the empty-and-just-opened state, so it never steals focus from
   * someone editing an existing caption. */
  React.useEffect(() => {
    if (caption === "" && document.activeElement !== captionRef.current) {
      captionRef.current?.focus();
    }
    // Only when the row opens: `caption` changing as the author types must not
    // re-focus, and it cannot anyway once the value is non-empty.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caption === ""]);

  const width = dragWidth ? `${dragWidth}px` : (storedWidth ?? undefined);

  /*
   * ONE tree, editable or not.
   *
   * The two states used to render different shapes — a bare `<figure><img>` at
   * rest against `<figure><span><img>…` while editing — so React rebuilt the
   * `<img>` element on every transition. A fresh element re-requests its src,
   * and an image served from an API that will not hand it back out of cache
   * blanked and redrew: the flicker on clicking into a description, and again
   * on the commit out of it. Same element, same request, nothing to redraw.
   *
   * What differs between the states is chrome that hangs off the image, never
   * the image itself: the resize handles (only ever drawn on a selection, which
   * a read-only editor cannot have) and the caption, which is an editable input
   * while editing and a plain `<figcaption>` at rest.
   */
  return (
    <NodeViewWrapper
      as="figure"
      ref={wrapperRef as React.Ref<HTMLElement>}
      className={cn(
        "rte-figure relative",
        /* The same class the serialised figure carries, so what is being
         * edited is laid out by the same rules as what is stored. */
        IMAGE_ALIGN_CLASS[align],
      )}
      /* Only while editing: ProseMirror owns the node's contents, and at rest
       * there is nothing here it needs to be told to keep its hands off. */
      contentEditable={editable ? false : undefined}
    >
      <span className="relative inline-block max-w-full">
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          style={{ width }}
          draggable={false}
        />
        {/* Pointer-only affordance, and marked as such: a drag handle cannot be
            operated from a keyboard however it is labelled. The keyboard route
            is Alt+←/→ while the image is selected, which `ResizableImage`
            registers as a real shortcut — see `resizable-image.ts`. */}
        {selected &&
          CORNERS.map((corner) => (
            <span
              key={corner.key}
              aria-hidden="true"
              role="presentation"
              onPointerDown={(e) => startResize(e, corner.dir)}
              className={cn(
                "absolute size-2.5 rounded-full bg-accent",
                corner.position,
                corner.cursor,
              )}
            />
          ))}
      </span>
      {selected && (
        <span className="sr-only">
          Image selected. Press Alt with the left or right arrow key to resize.
        </span>
      )}
      {/* Rendered on the caption's own existence, never on `selected`.
       *
       * It used to appear whenever the image was selected, which made a plain
       * click on an image grow the figure by a row and push every following
       * paragraph down — and shrink it again on the next click elsewhere. The
       * document flickered on every select. Captions are opened deliberately
       * now, from the image toolbar, and the row is in the flow in exactly the
       * states the stored HTML is: caption or no caption. */}
      {caption !== null &&
        (editable ? (
          <span className="mt-1 flex items-center gap-2">
            <input
              ref={captionRef}
              value={caption}
              placeholder="Add a caption…"
              /* Empty stays `""` while the row is open — writing `null` here
               * would delete the row out from under the caret the moment the
               * field was cleared. It is cleared to `null` on the way out
               * instead, by `closeEmptyCaption`. */
              onChange={(e) => updateAttributes({ caption: e.target.value })}
              /* `InlineEdit`'s wrapper reverts the whole field on Escape and
               * commits on Enter, so neither may reach it from in here. */
              onKeyDown={(e) => {
                if (e.key === "Escape" || e.key === "Enter") {
                  e.preventDefault();
                  e.stopPropagation();
                  closeEmptyCaption();
                  editor.commands.focus();
                }
              }}
              onBlur={closeEmptyCaption}
              data-bare-input=""
              aria-label="Image caption"
              className={cn(
                "w-full bg-transparent text-xs text-text-tertiary focus-visible:outline-none focus-fill",
                CAPTION_ALIGN[align],
              )}
            />
          </span>
        ) : (
          caption !== "" && <figcaption>{caption}</figcaption>
        ))}
    </NodeViewWrapper>
  );
}
